/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { ReactNode, useRef, useState } from 'react';

import { Panel } from 'primereact/panel';
import { classNames } from 'primereact/utils';

import { Button } from 'primereact/button';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { OGCIOTable } from '../components/OGCIOTable.tsx';
import { ExecuteProcessForm } from '../components/ExecuteProcessForm.tsx';

import { Job, linksDefinition, ProcessDescription, ProcessSummary } from '../types/processing-schemas.ts';
import { useProcessesContext } from '../contexts/ProcessesContext.tsx';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import { ButtonGroup } from 'primereact/buttongroup';

export interface ProcessViewerProps {
  className?: string;
  process: ProcessSummary;
  editLink?: string;
  permalink?: string;
  viewProcessDefinition?: boolean;
  cwlViewer?: ReactNode;
  extraInputsToIgnore?: string[];
  onRedirect?: (job: Job) => void;
  hrefFormatter?: (link: string) => Promise<string>;
  onImportJobExecutionContent?: (job: Job) => Job;
  onFormatProcessDescription?: (processDescription: ProcessDescription) => Promise<ProcessDescription>;
}

export const ProcessDetailViewer: React.FC<ProcessViewerProps> = (props) => {
  const { processesEndpoint } = useProcessesContext();

  const { t } = useTranslation();

  const [showDialog, setShowDialog] = useState(false);
  const [isPermalinkCopied, setIsPermalinkCopied] = useState(false);

  const toast = useRef<Toast>(null);

  const { data: processDesc, isFetching } = useQuery({
    queryKey: ['fetch_process_details', props.process, props.onFormatProcessDescription],
    queryFn: async () => {
      const self_link = props.process.links?.find((el) => el.rel === linksDefinition.process);
      if (!self_link?.href) {
        console.error('No self link found for process:', props.process);
        return null;
      }
      const processDescription = await processesEndpoint.get<ProcessDescription>(self_link.href);
      if (props.onFormatProcessDescription) {
        return props.onFormatProcessDescription(processDescription);
      }
      return processDescription;
    },
    refetchOnWindowFocus: false
  });

  const { mutateAsync } = useMutation({
    mutationKey: ['create_job', processDesc],
    mutationFn: async (data: Record<string, unknown>) => {
      if (!processDesc) {
        throw new Error('No active process found');
      }
      const execution_link = processDesc.links?.find((el) => el.rel === linksDefinition.execution);
      if (execution_link) {
        return processesEndpoint.post(execution_link.href, data);
      }
      const self_link = processDesc.links?.find((el) => el.rel === linksDefinition.self);
      if (self_link) {
        return processesEndpoint.post_process(self_link.href, data);
      }
      return processesEndpoint.post_process(props.process.id, data);
    }
  });

  const copyPermalink = async (permalink: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(permalink);
      setIsPermalinkCopied(true);
      setTimeout(() => {
        setIsPermalinkCopied(false);
      }, 1200);
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: t(['notifs.error.title'], 'Error'),
        detail: t(['notifs.error.description'], 'Operation declined'),
        contentClassName: 'text-left',
        className: 'text-left',
        life: 4000
      });
    }
  };

  const DescriptionPanelTemplate = ({
    onRun,
    editLink,
    permalink
  }: {
    onRun?: () => void;
    editLink?: string;
    permalink?: string;
  }): React.ReactNode => (
    <div className="flex justify-content-between w-full align-items-center flex-wrap surface-50 surface-border px-4 border-1 py-3 border-round-top">
      <span className="inline-block font-bold">Description</span>
      <div className="cursor-pointer sm:mt-2 xl:mt-0">
        <ButtonGroup>
          {onRun && (
            <Button
              aria-label={t(['actions.run', 'Run'])}
              onClick={onRun}
              size="small"
              severity="success"
              iconPos={'right'}
              label={t(['actions.run', 'Run'])}
              icon="pi pi-play"
            />
          )}
          {editLink && (
            <Button
              aria-label={t(['actions.edit_cwl', 'Edit'])}
              onClick={() => window.open(editLink, '_blank')}
              severity="contrast"
              size="small"
              iconPos={'right'}
              label={t(['actions.edit_cwl', 'Edit'])}
              icon="pi pi-pencil"
            />
          )}

          {props.viewProcessDefinition && (
            <Button
              aria-label={t(['actions.open_json_schema', 'JSON schema'])}
              onClick={async () => {
                const link = props.process.links?.find((el) => el.rel === linksDefinition.process);
                if (link?.href) {
                  const href = props.hrefFormatter ? await props.hrefFormatter(link.href) : link.href;
                  window.open(href);
                }
              }}
              severity="secondary"
              size="small"
              outlined
              className="border-x-none"
              iconPos={'right'}
              label={t(['actions.process_definition_api', 'JSON schema'])}
              icon="pi pi-external-link"
            />
          )}
          {permalink && (
            <Button
              outlined={!isPermalinkCopied}
              severity={isPermalinkCopied ? 'success' : 'contrast'}
              size="small"
              icon={isPermalinkCopied ? 'pi pi-check' : 'pi pi-copy'}
              tooltip={
                isPermalinkCopied
                  ? t(['actions.copied_permalink', 'Copied'])
                  : t(['actions.copy_permalink', 'Copy permalink'])
              }
              tooltipOptions={{ position: 'top' }}
              onClick={() => {
                copyPermalink(permalink);
              }}
            />
          )}
        </ButtonGroup>
      </div>
    </div>
  );

  return (
    <div
      className={classNames(
        props.className,
        'w-full mt-3 flex sm:flex-column md:flex-column sm:flex-column-reverse md:flex-column-reverse lg:flex-row xl:flex-row  align-content-stretch '
      )}
    >
      <Toast ref={toast} />

      {props.cwlViewer && props.cwlViewer}
      <div className={classNames(props.cwlViewer ? 'sm:col-12 md:col-12 lg:col-5 xl:col-5' : 'col-12 px-0')}>
        <Panel
          headerTemplate={
            <DescriptionPanelTemplate
              editLink={props.editLink}
              permalink={props.permalink}
              onRun={() => setShowDialog(true)}
            />
          }
          className={classNames('border-round text-left sm:px-0 md:px-0', {
            'lg:px-2 xl:px-2': !!props.cwlViewer
          })}
        >
          <div className="flex flex-column">
            <div className={'flex w-full'}>{props.process.description}</div>
          </div>
        </Panel>
        <Panel
          header={t(['misc.inputs', 'Inputs'])}
          className={classNames('border-round text-left mt-3 sm:px-0 md:px-0', {
            'lg:px-2 xl:px-2': !!props.cwlViewer
          })}
        >
          <OGCIOTable
            className="text-sm"
            loading={isFetching}
            extraInputsToIgnore={props.extraInputsToIgnore}
            content={processDesc?.inputs}
            type="inputs"
          />
        </Panel>

        <Panel
          header={t(['misc.outputs', 'Outputs'])}
          className={classNames('border-round text-left mt-3 sm:px-0 md:px-0', {
            'lg:px-2 xl:px-2': !!props.cwlViewer
          })}
        >
          <OGCIOTable className="text-sm" loading={isFetching} content={processDesc?.outputs} type="outputs" />
        </Panel>
      </div>

      {processDesc && (
        <Dialog
          header={processDesc.title}
          visible={showDialog}
          style={{ width: '60%', minHeight: '15rem' }}
          onHide={() => {
            if (!showDialog) {
              return;
            }
            setShowDialog(false);
          }}
        >
          <div style={{ minHeight: '25rem', height: '100%' }} className="w-full">
            <ExecuteProcessForm
              description={processDesc.description}
              inputs={processDesc?.inputs}
              onImportJobExecutionContent={props.onImportJobExecutionContent}
              onSubmit={({ formData }) => {
                return mutateAsync(formData).then(async (resp) => {
                  if (resp) {
                    setShowDialog(false);
                    toast.current?.show({
                      severity: 'success',
                      summary: t(['notifs.success.title'], 'Success'),
                      detail: (
                        <div className="flex flex-column">
                          <div>{t(['notifs.success.description'], 'Operation success')}</div>
                          <div
                            className="text-color underline cursor-pointer"
                            onClick={() => {
                              props.onRedirect?.(resp as Job);
                            }}
                          >
                            {t(['actions.see_job'], 'See more')}
                          </div>
                        </div>
                      ),
                      contentClassName: 'text-left',
                      className: 'text-left',
                      life: 2000000
                    });
                    return true;
                  } else {
                    toast.current?.show({
                      severity: 'error',
                      summary: t(['notifs.error.title'], 'Error'),
                      detail: t(['notifs.error.description'], 'Operation declined'),
                      contentClassName: 'text-left',
                      className: 'text-left',
                      life: 6000
                    });
                    return false;
                  }
                });
              }}
              links={processDesc.links}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};
